// Push permission for Settings → Notifications and the pre-prompt
// (Mantis 13084 / 13085), on the web and inside the app.
//
// Web: window.QushiWebPush (public/index.html) wraps the browser's
// Notification permission and the Firebase token.
// App: Sagar's bridge on the same 'Qushi' handler -
//   notification_permission_status  -> { status, can_prompt, fcm_token? }
//   notification_permission_request -> { result, status, can_prompt, fcm_token? }
//   notification_settings_open      -> { opened }
// and window event 'qushiNotificationPermission' on app resume.
// App status: not_asked | granted | denied | blocked.
//
// Everything is reduced to one state for the page:
//   granted      - push can be delivered; no line
//   prompt       - "Push is off on this device. [Turn on]"
//   blocked      - the OS/browser will not ask again; settings message
//   unsupported  - this browser has no web push; no line

import { event } from "./event";
import { registerFcmToken } from "../api/user";

/* global isFlutterInAppWebViewReady */
export const isInApp = () =>
  typeof isFlutterInAppWebViewReady !== "undefined" && !!isFlutterInAppWebViewReady;

// callHandler resolves with whatever Dart returns; accept an object or JSON.
const parse = (value) => {
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch (e) {
      return {};
    }
  }
  return value || {};
};

const fromApp = ({ status, can_prompt }) => {
  // An app build without the bridge answers nothing; show no line.
  if (!status) return "unsupported";
  if (status === "granted") return "granted";
  return can_prompt ? "prompt" : "blocked";
};

const fromWeb = (permission) => {
  if (permission === "granted") return "granted";
  if (permission === "denied") return "blocked";
  return "prompt";
};

let lastRegistered = null;
// The app hands its token to the page (it has no login of its own); the page
// registers it against the current login. The web gets it from Firebase.
// Keyed by login too: after logout/login the same token belongs to a new row.
const register = async (token) => {
  const login = localStorage.getItem("accessToken");
  if (!token || token === "null" || !login) return;
  if (lastRegistered === token + login) return;
  try {
    await registerFcmToken({
      fcm_token: token,
      device_type: isInApp() ? undefined : "W",
    });
    lastRegistered = token + login;
  } catch (e) {
    console.log("FCM token register failed", e);
  }
};

let syncedLogin = null;
// Once per login: read the permission (no prompt) so a granted device's token
// is registered. Called on every route change; does nothing until the login
// changes.
export function syncPushToken() {
  const login = localStorage.getItem("accessToken");
  if (!login || login === syncedLogin) return;
  syncedLogin = login;
  getPushPermission();
}

// On iOS the token only exists after permission is granted, so it often comes
// later in the app's event rather than in the request reply. Listen for it on
// every screen, not just Settings.
window.addEventListener("qushiNotificationPermission", (e) => register(parse(e?.detail).fcm_token));

const webPush = () => window.QushiWebPush;

export async function getPushPermission() {
  if (isInApp()) {
    const reply = parse(await event("notification_permission_status"));
    register(reply.fcm_token);
    return fromApp(reply);
  }
  if (!webPush() || !(await webPush().supported())) return "unsupported";
  const state = fromWeb(webPush().permission());
  if (state === "granted") register(await webPush().token());
  return state;
}

// Opens the browser / OS prompt. Resolves { result, state } where result is
// allowed | denied | dismissed | not_prompted.
export async function requestPushPermission() {
  if (isInApp()) {
    const reply = parse(await event("notification_permission_request"));
    register(reply.fcm_token);
    return { result: reply.result || "not_prompted", state: fromApp(reply) };
  }
  if (!webPush() || !(await webPush().supported())) {
    return { result: "not_prompted", state: "unsupported" };
  }
  const permission = await webPush().request();
  const state = fromWeb(permission);
  if (state === "granted") register(await webPush().token());
  const result =
    permission === "granted" ? "allowed" : permission === "denied" ? "denied" : "dismissed";
  return { result, state };
}

export function openPushSettings() {
  return event("notification_settings_open");
}

// Calls back with the new state when it can change outside the page: the app
// resuming from phone settings, or the browser permission being changed.
export function onPushPermissionChange(callback) {
  const onApp = (e) => callback(fromApp(parse(e?.detail)));
  window.addEventListener("qushiNotificationPermission", onApp);

  let permissionStatus = null;
  const onWeb = () => callback(fromWeb(permissionStatus.state === "prompt" ? "default" : permissionStatus.state));
  if (!isInApp() && navigator.permissions?.query) {
    navigator.permissions
      .query({ name: "notifications" })
      .then((status) => {
        permissionStatus = status;
        status.addEventListener("change", onWeb);
      })
      .catch(() => {});
  }

  return () => {
    window.removeEventListener("qushiNotificationPermission", onApp);
    permissionStatus?.removeEventListener("change", onWeb);
  };
}
